<script setup>
import { computed, ref, watch } from 'vue'
import { backtestApi } from '@/api/backtest'
import { toast } from '@/composables/useToast'

const props = defineProps({
  visible: { type: Boolean, default: false }
})

const emit = defineEmits(['update:visible'])

const segments = ref([])
const currentVersion = ref(1)
const loading = ref(false)
const saving = ref(false)
const polishing = ref(false)
const restoring = ref(false)
const focusNote = ref('')
const saveNote = ref('')

const view = ref('edit') // edit | history | preview
const historyList = ref([])
const previewSegments = ref([])
const previewVersion = ref(null)

const editableSegments = computed(() => segments.value.filter(s => s.editable))
const protectedSegments = computed(() => segments.value.filter(s => !s.editable))

watch(() => props.visible, (val) => {
  if (val) {
    focusNote.value = ''
    saveNote.value = ''
    view.value = 'edit'
    load()
  }
})

async function load () {
  loading.value = true
  try {
    const data = await backtestApi.getPromptSegments()
    currentVersion.value = data.version || 1
    segments.value = (data.segments || []).map(s => ({ ...s, draft: s.content }))
  } catch (e) {
    toast.error('加载提示词失败: ' + (e.message || '未知错误'))
  } finally {
    loading.value = false
  }
}

function onClose () {
  if (!saving.value && !polishing.value && !restoring.value) {
    emit('update:visible', false)
  }
}

async function onSave () {
  const payload = editableSegments.value.map(s => ({
    segment_key: s.segment_key,
    content: s.draft
  }))
  if (!payload.length) {
    toast.warning('没有可保存的分段')
    return
  }
  saving.value = true
  try {
    const res = await backtestApi.savePromptSegments(payload, saveNote.value.trim())
    const newVersion = res && res.version ? res.version : currentVersion.value
    currentVersion.value = newVersion
    segments.value.forEach(s => {
      if (s.editable) s.content = s.draft
    })
    toast.success(`已保存为 v${newVersion}，新的回测将使用该提示词`)
    emit('update:visible', false)
  } catch (e) {
    toast.error('保存失败: ' + (e.message || '未知错误'))
  } finally {
    saving.value = false
  }
}

async function onPolish () {
  const payload = editableSegments.value.map(s => ({
    segment_key: s.segment_key,
    content: s.draft
  }))
  if (!payload.length) {
    toast.warning('没有可润色的分段')
    return
  }
  polishing.value = true
  try {
    const res = await backtestApi.polishPromptSegments(payload, focusNote.value.trim())
    let count = 0
    for (const seg of segments.value) {
      const polished = res && res.polished ? res.polished[seg.segment_key] : null
      if (polished) {
        seg.draft = polished
        count++
      }
    }
    toast.success(`AI 润色完成，更新 ${count} 个分段；润色结果尚未保存，确认后点保存`)
  } catch (e) {
    toast.error('润色失败: ' + (e.message || '未知错误'))
  } finally {
    polishing.value = false
  }
}

async function openHistory () {
  view.value = 'history'
  historyList.value = []
  try {
    historyList.value = await backtestApi.getPromptVersions()
  } catch (e) {
    toast.error('加载历史版本失败: ' + (e.message || '未知错误'))
  }
}

async function viewVersion (version) {
  try {
    const data = await backtestApi.getPromptVersion(version)
    previewVersion.value = data.version
    previewSegments.value = data.segments || []
    view.value = 'preview'
  } catch (e) {
    toast.error('加载版本详情失败: ' + (e.message || '未知错误'))
  }
}

async function restoreVersion (version) {
  restoring.value = true
  try {
    const res = await backtestApi.restorePromptVersion(version)
    const newVersion = res && res.version ? res.version : version + 1
    toast.success(`已回滚到 v${version}，当前版本 v${newVersion}`)
    await load()
    view.value = 'edit'
  } catch (e) {
    toast.error('回滚失败: ' + (e.message || '未知错误'))
  } finally {
    restoring.value = false
  }
}
</script>

<template>
  <div v-if="visible" class="modal-overlay" @click.self="onClose">
    <div class="modal-panel">
      <div class="modal-header">
        <h3 class="modal-title">
          回测提示词设置
          <span class="version-badge">当前 v{{ currentVersion }}</span>
        </h3>
        <div class="header-actions">
          <button v-if="view === 'edit'" class="btn-ghost" @click="openHistory">历史版本</button>
          <button class="modal-close" @click="onClose">&times;</button>
        </div>
      </div>

      <div v-if="loading" class="modal-body loading-body">
        <span class="loading-text">加载中...</span>
      </div>

      <!-- 编辑视图 -->
      <div v-else-if="view === 'edit'" class="modal-body">
        <div class="hint">
          可编辑分段可直接修改并保存，每次保存生成新版本（当前 v{{ currentVersion }}）；受保护分段（输出格式、字段说明）与引擎解析契约绑定，不允许修改。可在历史版本中随时回滚。
        </div>

        <div class="input-row">
          <div class="focus-row">
            <label>润色重点（可选）</label>
            <input
              v-model="focusNote"
              type="text"
              placeholder="例如：更强调板块龙头、严格现金纪律"
            >
          </div>
          <div class="focus-row">
            <label>版本备注（可选）</label>
            <input
              v-model="saveNote"
              type="text"
              placeholder="例如：v2 版买入策略调整"
            >
          </div>
        </div>

        <div
          v-for="seg in editableSegments"
          :key="seg.segment_key"
          class="segment-block"
        >
          <div class="segment-head">
            <span class="segment-title">{{ seg.title }}</span>
            <span class="badge editable">可编辑</span>
          </div>
          <textarea v-model="seg.draft" rows="4" class="segment-textarea"></textarea>
        </div>

        <div class="protected-title">
          受保护分段（只读）
        </div>
        <div
          v-for="seg in protectedSegments"
          :key="seg.segment_key"
          class="segment-block"
        >
          <div class="segment-head">
            <span class="segment-title">{{ seg.title }}</span>
            <span class="badge protected">受保护</span>
          </div>
          <textarea :value="seg.content" rows="3" class="segment-textarea readonly" readonly></textarea>
        </div>
      </div>

      <!-- 历史版本列表 -->
      <div v-else-if="view === 'history'" class="modal-body">
        <div class="hint">
          共 {{ historyList.length }} 个版本，点击「查看」预览快照，点击「恢复」将当前提示词回滚到该版本并生成新版本。
        </div>
        <div v-if="!historyList.length" class="empty-tip">暂无历史版本</div>
        <div v-for="v in historyList" :key="v.version" class="version-row">
          <div class="version-info">
            <span class="version-no">v{{ v.version }}</span>
            <span class="version-note">{{ v.note || '无备注' }}</span>
            <span class="version-time">{{ v.created_at }}</span>
            <span v-if="v.version === currentVersion" class="badge editable">当前</span>
          </div>
          <div class="version-actions">
            <button class="btn-ghost" :disabled="restoring" @click="viewVersion(v.version)">查看</button>
            <button class="btn-restore" :disabled="restoring || v.version === currentVersion" @click="restoreVersion(v.version)">
              {{ restoring ? '恢复中...' : '恢复' }}
            </button>
          </div>
        </div>
      </div>

      <!-- 版本快照预览 -->
      <div v-else class="modal-body">
        <div class="hint">
          版本 v{{ previewVersion }} 快照（只读）——「恢复此版本」将把当前提示词回滚为该快照。
        </div>
        <div
          v-for="seg in previewSegments"
          :key="seg.segment_key"
          class="segment-block"
        >
          <div class="segment-head">
            <span class="segment-title">{{ seg.title }}</span>
            <span class="badge" :class="seg.editable ? 'editable' : 'protected'">
              {{ seg.editable ? '可编辑' : '受保护' }}
            </span>
          </div>
          <textarea :value="seg.content" rows="4" class="segment-textarea readonly" readonly></textarea>
        </div>
      </div>

      <div class="modal-footer">
        <template v-if="view === 'edit'">
          <button class="btn-link" :disabled="saving || polishing" @click="onClose">取消</button>
          <button class="btn-primary polish" :disabled="saving || polishing" @click="onPolish">
            {{ polishing ? '润色中...' : 'AI 润色' }}
          </button>
          <button class="btn-primary" :disabled="saving || polishing" @click="onSave">
            {{ saving ? '保存中...' : '保存' }}
          </button>
        </template>
        <template v-else-if="view === 'history'">
          <button class="btn-link" @click="view = 'edit'">返回编辑</button>
        </template>
        <template v-else>
          <button class="btn-link" :disabled="restoring" @click="view = 'history'">返回列表</button>
          <button class="btn-primary" :disabled="restoring" @click="restoreVersion(previewVersion)">
            {{ restoring ? '恢复中...' : '恢复此版本' }}
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-panel {
  background: var(--bg-secondary);
  border: 1px solid var(--rule);
  border-radius: 10px;
  width: 780px;
  max-width: 96vw;
  max-height: 92vh;
  box-shadow: var(--shadow-soft);
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--rule);
}

.modal-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 10px;
}

.version-badge {
  font-size: 11px;
  font-weight: 500;
  color: var(--accent);
  border: 1px solid var(--accent);
  border-radius: 10px;
  padding: 2px 8px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.modal-close {
  background: transparent;
  border: none;
  color: var(--text-muted);
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }
}

.btn-ghost {
  padding: 6px 12px;
  border: 1px solid var(--rule);
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  color: var(--text-secondary);
  background: transparent;

  &:hover:not(:disabled) {
    border-color: var(--accent);
    color: var(--accent);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.modal-body {
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1;
  overflow-y: auto;
}

.loading-body {
  align-items: center;
  justify-content: center;
  min-height: 240px;
}

.loading-text {
  color: var(--text-muted);
  font-size: 14px;
}

.hint {
  font-size: 12px;
  line-height: 1.6;
  color: var(--text-muted);
  background: var(--bg-tertiary);
  border: 1px solid var(--rule);
  border-radius: 6px;
  padding: 8px 10px;
}

.empty-tip {
  color: var(--text-muted);
  font-size: 13px;
  text-align: center;
  padding: 24px 0;
}

.input-row {
  display: flex;
  gap: 14px;

  .focus-row {
    flex: 1;
  }
}

.focus-row {
  display: flex;
  align-items: center;
  gap: 10px;

  label {
    font-size: 13px;
    color: var(--text-secondary);
    white-space: nowrap;
  }

  input {
    flex: 1;
    min-width: 0;
    padding: 7px 10px;
    border: 1px solid var(--rule);
    border-radius: 6px;
    background: var(--bg-primary);
    color: var(--text-primary);
    font-size: 13px;
  }
}

.segment-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.segment-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.segment-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;

  &.editable {
    color: var(--accent);
    border: 1px solid var(--accent);
  }

  &.protected {
    color: var(--text-muted);
    border: 1px solid var(--rule);
  }
}

.segment-textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid var(--rule);
  border-radius: 6px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 13px;
  line-height: 1.6;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--accent);
  }

  &.readonly {
    opacity: 0.65;
    cursor: not-allowed;
  }
}

.protected-title {
  margin-top: 6px;
  padding-top: 12px;
  border-top: 1px dashed var(--rule);
  font-size: 12px;
  color: var(--text-muted);
}

.version-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid var(--rule);
  border-radius: 8px;
  background: var(--bg-primary);
}

.version-info {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex-wrap: wrap;
}

.version-no {
  font-weight: 600;
  font-size: 13px;
  color: var(--text-primary);
}

.version-note {
  font-size: 12px;
  color: var(--text-secondary);
}

.version-time {
  font-size: 12px;
  color: var(--text-muted);
}

.version-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.btn-restore {
  padding: 6px 12px;
  border: 1px solid var(--accent);
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  color: var(--accent);
  background: transparent;

  &:hover:not(:disabled) {
    background: var(--accent);
    color: #fff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid var(--rule);
}

.btn-link {
  background: transparent;
  border: none;
  color: var(--text-muted);
  font-size: 13px;
  cursor: pointer;
  padding: 7px 12px;

  &:hover {
    color: var(--text-primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.btn-primary {
  padding: 7px 18px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  color: #fff;
  background: var(--accent);

  &.polish {
    background: transparent;
    color: var(--accent);
    border: 1px solid var(--accent);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

/* ==================== 移动端适配 ==================== */
@media (max-width: 640px) {
  .modal-overlay {
    align-items: flex-end;
  }

  .modal-panel {
    width: 100vw;
    max-width: 100vw;
    height: 92vh;
    max-height: 92vh;
    border-radius: 12px 12px 0 0;
  }

  .input-row {
    flex-direction: column;
    gap: 10px;
  }

  .focus-row {
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
  }

  .version-row {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .version-actions {
    width: 100%;

    button {
      flex: 1;
    }
  }

  .modal-footer {
    flex-wrap: wrap;

    .btn-primary {
      flex: 1;
    }
  }
}
</style>
